// M004-F0613 / T-M004-0020 — in-memory dialog draft store.
//
// Design authority: docs/design/M004-D016-Dialog-and-Modal-Interaction-Standard.md §5, §6
// (studioaeye-Core). Do not relitigate the rules encoded here — they are normative:
//
//   - In-memory only. NEVER localStorage, NEVER sessionStorage (§6). A storage-backed
//     draft would survive logout and be readable by any script on the origin.
//   - Scope key is `{dialogId}:{recordId|"new"}` (§5.2) so two different records of the
//     same dialog type — or "new" vs. an edit of an existing record — never collide.
//   - Sensitive fields (password, one-time/2SV codes, API keys/tokens) must NEVER be
//     captured, detected by input type/attribute rather than a per-dialog field-name
//     allowlist (§6), because an allowlist silently fails the moment a new sensitive
//     field is added and nobody remembers to update it.
//   - Cleared synchronously on logout/session change (§6) — see clearAllDrafts(), wired
//     into AuthContext's logout().
//
// The store is a plain module-level Map. It intentionally has no dependency on React so
// it can be unit-tested directly (T-M004-0020 case 5 requires inspecting the store's
// contents directly, not just the rendered DOM) and so the "never localStorage" guarantee
// is structural: this file never references window.localStorage or window.sessionStorage.

const draftStore = new Map();

/** Build the `{dialogId}:{recordId|"new"}` scope key per M004-D016 §5.2. */
export function buildDraftScopeKey(dialogId, recordId) {
  const safeRecordId = recordId === undefined || recordId === null || recordId === ''
    ? 'new'
    : String(recordId);
  return `${dialogId}:${safeRecordId}`;
}

// Field types that can never be captured, regardless of name. This is the primary,
// structural defence: any current or future field with type="password" is excluded
// automatically, with zero per-dialog configuration.
const SENSITIVE_TYPES = new Set(['password']);

// autocomplete values are a standard, attribute-level (not name-based) signal browsers
// themselves use to identify credential and one-time-code fields.
const SENSITIVE_AUTOCOMPLETE = new Set([
  'current-password',
  'new-password',
  'one-time-code',
]);

// Belt-and-braces semantic fallback, per M004-D016 §6's own wording ("any field whose
// semantic name matches password|passphrase|secret"). This supplements, and never
// replaces, the type/attribute checks above — it is a regex over *meaning*, not a fixed
// list of specific field names to remember to update.
const SENSITIVE_NAME_PATTERN = /password|passphrase|secret|token|otp|totp|mfa|2sv|step[-_]?up|verification[-_]?code|security[-_]?code|recovery[-_]?code|backup[-_]?code/i;

/**
 * Whether a form field must never be captured into a draft. Detection is by the field's
 * own type/attributes (§6), not a hardcoded per-dialog allowlist:
 *   1. type="password" — catches every password field automatically, including one that
 *      toggles between "password" and "text" for a show/hide affordance (see also #3).
 *   2. an explicit data-draft-exclude attribute — the escape hatch M004-D016 §6 names for
 *      fields that aren't type="password" but are still sensitive (TOTP/step-up codes).
 *   3. a sensitive autocomplete value.
 *   4. a semantic name/id match (password/secret/token/otp/... ) — catches fields such as
 *      a Home-Assistant "Long-Lived Access Token" input that is sometimes rendered with
 *      type="text" (a reveal toggle) and would otherwise slip past check #1.
 */
export function isSensitiveField(element) {
  if (!element) return false;

  const type = String(element.type || '').toLowerCase();
  if (SENSITIVE_TYPES.has(type)) return true;

  const hasExcludeAttr = typeof element.hasAttribute === 'function'
    ? element.hasAttribute('data-draft-exclude')
    : Boolean(element.dataset && 'draftExclude' in element.dataset);
  if (hasExcludeAttr) return true;

  const autoComplete = String(
    (element.autocomplete !== undefined ? element.autocomplete : element.getAttribute?.('autocomplete')) || ''
  ).toLowerCase();
  if (autoComplete && SENSITIVE_AUTOCOMPLETE.has(autoComplete)) return true;

  const semanticName = String(element.name || element.id || '').toLowerCase();
  if (semanticName && SENSITIVE_NAME_PATTERN.test(semanticName)) return true;

  return false;
}

const readFieldValue = (element) => {
  const type = String(element.type || '').toLowerCase();
  if (type === 'checkbox') return Boolean(element.checked);
  return element.value;
};

/**
 * Capture a single field's current value into the draft for `scopeKey`, unless the field
 * is sensitive (checked first — a sensitive field's value is never even read into a local
 * variable, let alone written to the store) or has no stable key (name/id) to restore by.
 */
export function captureFieldFromElement(scopeKey, element) {
  if (!scopeKey || !element) return;
  if (isSensitiveField(element)) return;

  const fieldKey = element.name || element.id;
  if (!fieldKey) return;

  const existing = draftStore.get(scopeKey) || {};
  draftStore.set(scopeKey, { ...existing, [fieldKey]: readFieldValue(element) });
}

/** Read the draft object for a scope key, or null if none exists. */
export function getDraftForScope(scopeKey) {
  if (!scopeKey) return null;
  return draftStore.get(scopeKey) || null;
}

/** Discard the draft for one scope key — called on successful submit (§5.2), never on an incidental close. */
export function clearDraftForScope(scopeKey) {
  if (!scopeKey) return;
  draftStore.delete(scopeKey);
}

/** Discard every draft — called synchronously from logout/session-teardown (§6). */
export function clearAllDrafts() {
  draftStore.clear();
}

/** Whether any draft currently exists — used to assert the store is empty post-logout. */
export function hasAnyDrafts() {
  return draftStore.size > 0;
}
